import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, LockIcon, SearchIcon, UserPlusIcon } from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { realtors } from '../../data/realtors';
import type { Realtor } from '../../types/portal';
interface AddRealtorDrawerProps {
  open: boolean;
  onClose: () => void;
  onToast: (message: string) => void;
}

/**
 * Duplicate prevention: the loan officer must search the shared partner
 * database before a new record can be created. House accounts are locked.
 */
export function AddRealtorDrawer({
  open,
  onClose,
  onToast
}: AddRealtorDrawerProps) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < 2) return [];
    return realtors.filter(realtor => realtor.name.toLowerCase().includes(term) || realtor.company.toLowerCase().includes(term) || realtor.email.toLowerCase().includes(term));
  }, [query]);
  const searched = query.trim().length >= 2;
  const noMatches = searched && matches.length === 0;
  return <Drawer open={open} onClose={() => {
    setQuery('');
    setCreating(false);
    onClose();
  }} eyebrow="Shared partner database" title="Add a Realtor or partner" subtitle="Search first — every Realtor is shared company-wide so duplicates can’t be created." data-id="element-477" data-mp="3la8dhsl7awgc">
      <label htmlFor="realtor-search" className="sr-only" data-id="element-478" data-mp="32iza3sp3wvl">
        Search existing Realtors
      </label>
      <div className="relative" data-id="element-479" data-mp="cu6mfsi7yrr9">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-400" aria-hidden="true" data-id="element-480" data-mp="ctwva1lfuejz" />
        <input id="realtor-search" type="search" value={query} onChange={event => {
        setQuery(event.target.value);
        setCreating(false);
      }} placeholder="Name, brokerage, or email" className="h-11 w-full rounded-lg border border-navy-200 pl-9 pr-3 text-sm text-navy-900 placeholder:text-navy-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-481" data-mp="mscn66sahruj" />
      </div>

      {!searched ? <p className="mt-4 rounded-xl bg-navy-50 p-4 text-sm text-navy-600" data-id="element-482" data-mp="2rwfje6xnwjo7">
          Type at least two characters. If the partner already exists you’ll log
          your activity against their record instead of creating a new one.
        </p> : null}

      {matches.length > 0 ? <div className="mt-4 space-y-3" data-id="element-483" data-mp="3flroaulyi5kp">
          <p className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-484" data-mp="3fkqeetgofp4k">
            {matches.length} existing match{matches.length === 1 ? '' : 'es'}
          </p>
          {matches.map(realtor => <MatchCard key={realtor.id} realtor={realtor} onToast={onToast} data-id="element-485" data-mp="1l88v1wyyl957" />)}
        </div> : null}

      {noMatches && !creating ? <div className="mt-4 rounded-xl border border-navy-100 bg-white p-5 text-center" data-id="element-486" data-mp="171ot7l16p51v">
          <UserPlusIcon className="mx-auto h-6 w-6 text-navy-400" aria-hidden="true" data-id="element-487" data-mp="1b3k67msh96vh" />
          <h3 className="mt-2 text-sm font-semibold text-navy-800" data-id="element-488" data-mp="xd6vjco7be2g">
            No match for “{query.trim()}”
          </h3>
          <p className="mt-1 text-sm text-navy-500" data-id="element-489" data-mp="129xc013x4boe">
            This partner isn’t in the database yet. You can create the record
            and start logging activity against it.
          </p>
          <button type="button" onClick={() => setCreating(true)} className="mt-4 inline-flex h-11 items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-490" data-mp="se3u027fjpz8">
            Create new partner
          </button>
        </div> : null}

      {creating ? <form className="mt-4 space-y-4" onSubmit={event => {
      event.preventDefault();
      onToast(`${query.trim()} added to the shared partner database.`);
      setQuery('');
      setCreating(false);
      onClose();
    }} data-id="element-491" data-mp="2dqdj975bjm06">
          <Field label="Full name" defaultValue={query.trim()} id="new-name" data-id="element-492" data-mp="3pyaharvitgl" />
          <Field label="Brokerage or company" id="new-company" data-id="element-493" data-mp="3s2oiljhl8ul" />
          <div data-id="element-494" data-mp="3vtha78g2swv9">
            <label htmlFor="new-type" className="block text-sm font-semibold text-navy-800" data-id="element-495" data-mp="3vtqxupdnerxs">
              Partner type
            </label>
            <select id="new-type" className="mt-1.5 h-11 w-full rounded-lg border border-navy-200 bg-white px-3 text-sm text-navy-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-496" data-mp="3m2mn61v09t8r">
              <option data-id="element-497" data-mp="3846ggax582qm">Realtor</option>
              <option data-id="element-498" data-mp="3834uvcii3xun">Title partner</option>
              <option data-id="element-499" data-mp="335d26ga187pe">Insurance partner</option>
            </select>
          </div>
          <Field label="Mobile phone" id="new-phone" type="tel" data-id="element-500" data-mp="337g0rop9395q" />
          <Field label="Email" id="new-email" type="email" data-id="element-501" data-mp="1imj0b4r2bp8m" />
          <p className="rounded-lg bg-warn-50 px-3 py-2 text-xs font-medium text-warn-700 ring-1 ring-warn-200" data-id="element-502" data-mp="1nm5dje6w55hf">
            New partners are visible to every loan officer. Ownership is based
            on logged communication, not on who created the record.
          </p>
          <button type="submit" className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-503" data-mp="1wkzflk2zqo2d">
            Save partner
          </button>
        </form> : null}
    </Drawer>;
}
function MatchCard({
  realtor,
  onToast
}: {
  realtor: Realtor;
  onToast: (message: string) => void;
}) {
  const locked = realtor.access === 'house-locked';
  return <div className={`rounded-xl border p-4 ${locked ? 'border-navy-200 bg-navy-50' : 'border-navy-100 bg-white'}`} data-id="element-504" data-mp="g8anck0poqbi">
      <div className="flex flex-wrap items-center gap-2" data-id="element-505" data-mp="1oiub2qej44f0">
        <p className="text-sm font-semibold text-navy-900" data-id="element-506" data-mp="1oh9g4zoayjgm">{realtor.name}</p>
        {locked ? <span className="inline-flex items-center gap-1 rounded-full bg-navy-800 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white" data-id="element-507" data-mp="1jj7ymj6iys79">
            <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-508" data-mp="1t913l1c5w92j" />
            House account
          </span> : null}
      </div>
      <p className="mt-1 text-sm text-navy-600" data-id="element-509" data-mp="15ki3h939n2lj">{realtor.company}</p>
      <p className="mt-1 text-xs text-navy-500" data-id="element-510" data-mp="10lnc3ir8g5pt">
        {realtor.id} · {realtor.market} · {realtor.referralsYtd} referrals YTD
      </p>

      {locked ? <p className="mt-3 flex items-start gap-1.5 text-xs font-medium text-navy-700" data-id="element-511" data-mp="1eia91lj6qvvq">
          <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-512" data-mp="19no6m7v30dns" />
          This partner is managed by the Reach house team. You can see the
          record, but you can’t claim it or log activity against it.
        </p> : <button type="button" onClick={() => onToast(`${realtor.name} added to your list — log a touch to keep it there.`)} className="mt-3 inline-flex h-11 items-center justify-center rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-513" data-mp="2tgfwxf3wdsqd">
          Use this record
        </button>}
    </div>;
}
function Field({
  label,
  id,
  type = 'text',
  defaultValue
}: {
  label: string;
  id: string;
  type?: string;
  defaultValue?: string;
}) {
  return <div data-id="element-514" data-mp="32j795h4lksj2">
      <label htmlFor={id} className="block text-sm font-semibold text-navy-800" data-id="element-515" data-mp="32hdyzrurzq6b">
        {label}
      </label>
      <input id={id} type={type} defaultValue={defaultValue} className="mt-1.5 h-11 w-full rounded-lg border border-navy-200 px-3 text-sm text-navy-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200" data-id="element-516" data-mp="37fxou3chpm3y" />
    </div>;
}